import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Play, Pause, Calculator, Workflow, Users, ShieldAlert,
  LineChart, Coins, ArrowRight, ChevronLeft, ChevronRight, Activity,
} from 'lucide-react';

/**
 * 30-second guided demo modal — auto-advances through 6 slides (5s each)
 * showing how a sample highway project flows through the BuildMetri.AI suite.
 *
 * Pre-canned numbers (no API calls) so it loads instantly and is reproducible.
 *  - Project: Infra / Highway, GFA 82,000 m², region IN, 18 months, EPC contract
 *  - Total: $14,820,310 USD
 */

const SLIDE_MS = 5000;
const TOTAL_MS = 30_000;

const SAMPLE = {
  project: 'Highway Corridor — 12 km',
  sector: 'Infrastructure / Highways',
  region: 'IN · Mumbai',
  gfa: '82,000',
  months: 18,
  contract: 'EPC',
  total_usd: '$14,820,310',
};

const PHASES = [
  { code: '01', name: 'Concept', pct: 4 },
  { code: '02', name: 'FEED',    pct: 8 },
  { code: '03', name: 'Detailed Design', pct: 14 },
  { code: '04', name: 'Procurement',     pct: 12 },
  { code: '05', name: 'Mobilisation',    pct: 6 },
  { code: '06', name: 'Construction',    pct: 38 },
  { code: '07', name: 'Commissioning',   pct: 10 },
  { code: '08', name: 'Handover',        pct: 8 },
];

const RESOURCES = [
  { role: 'Project manager',    fte: 1,  rate: '$185/h' },
  { role: 'Lead structural eng.', fte: 2, rate: '$140/h' },
  { role: 'BIM coordinator',    fte: 3,  rate: '$95/h'  },
  { role: 'Site supervisor',    fte: 4,  rate: '$72/h'  },
  { role: 'Crew (mixed trades)', fte: 38, rate: '$28/h' },
];

const RISKS = [
  { code: 'R-01', label: 'Monsoon delay (3 wk)',     impact: '+$680k', sev: 'med' },
  { code: 'R-02', label: 'Steel price volatility',   impact: '+$420k', sev: 'med' },
  { code: 'R-03', label: 'Right-of-way slippage',    impact: '+$310k', sev: 'low' },
  { code: 'R-04', label: 'Geotechnical surprise',    impact: '+$540k', sev: 'high' },
];

const EVM = [
  { k: 'PV',  v: '$8.20M',  hint: 'Planned value · M-10' },
  { k: 'EV',  v: '$7.85M',  hint: 'Earned value' },
  { k: 'AC',  v: '$8.42M',  hint: 'Actual cost' },
  { k: 'CPI', v: '0.93',    hint: 'Cost performance', warn: true },
  { k: 'SPI', v: '0.96',    hint: 'Schedule perf.' },
  { k: 'EAC', v: '$15.94M', hint: 'Estimate at completion' },
];

const FUNDING = [
  { m: 'M1', plan: 410,  actual: 380 },
  { m: 'M3', plan: 720,  actual: 770 },
  { m: 'M6', plan: 1080, actual: 1140 },
  { m: 'M9', plan: 1450, actual: 1620 },
  { m: 'M12',plan: 1380, actual: 1310 },
  { m: 'M15',plan: 1010, actual: 980  },
  { m: 'M18',plan: 540,  actual: 520  },
];

const SLIDES = [
  { id: 'calc', icon: Calculator,  title: 'Calculate',  badge: '01 / Calculator', kicker: 'Region-aware estimate in seconds' },
  { id: 'wbs',  icon: Workflow,    title: 'Decompose',  badge: '02 / WBS',        kicker: 'Eight phases, weighted by sub-sector' },
  { id: 'res',  icon: Users,       title: 'Plan crew',  badge: '03 / Resources',  kicker: 'FTEs and labour rates by region' },
  { id: 'risk', icon: ShieldAlert, title: 'Quantify risk', badge: '04 / Risk',    kicker: 'Auto-included risks &amp; contingency' },
  { id: 'evm',  icon: LineChart,   title: 'Track EVM',  badge: '05 / EVM',        kicker: 'PV · EV · AC · CPI · SPI · EAC' },
  { id: 'fund', icon: Coins,       title: 'Re-baseline funding', badge: '06 / Funding', kicker: 'AI variance recalculates monthly burn' },
];

/* ---------------------------------------------------------------- *
 *  Slide bodies
 * ---------------------------------------------------------------- */
function SlideCalc() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-px bg-background/15 border border-background/15">
        {[
          ['Project',  SAMPLE.project],
          ['Sector',   SAMPLE.sector],
          ['Region',   SAMPLE.region],
          ['GFA',      `${SAMPLE.gfa} m²`],
          ['Duration', `${SAMPLE.months} months`],
          ['Contract', SAMPLE.contract],
        ].map(([k, v]) => (
          <div key={k} className="bg-foreground p-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-background/55">{k}</div>
            <div className="text-sm font-semibold text-background mt-0.5">{v}</div>
          </div>
        ))}
      </div>
      <div className="border border-background/20 p-4 bg-background/5">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-background/55">Total estimated cost</div>
        <div className="font-display text-4xl font-bold text-emerald-glow mt-1">{SAMPLE.total_usd}</div>
        <div className="font-mono text-[11px] text-background/65 mt-1">USD · region-calibrated · 11 multipliers applied</div>
      </div>
    </div>
  );
}

function SlideWBS() {
  return (
    <div className="space-y-1">
      {PHASES.map((p, i) => (
        <motion.div key={p.code}
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.06 }}
          className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border border-background/15 px-3 py-2 bg-background/5">
          <span className="font-mono text-[10px] text-background/55">{p.code}</span>
          <div>
            <div className="text-sm font-semibold text-background">{p.name}</div>
            <div className="mt-1 h-1 bg-background/15">
              <motion.div className="h-1 bg-emerald-glow" style={{ backgroundColor: 'hsl(151 100% 45%)' }}
                initial={{ width: 0 }} animate={{ width: `${p.pct * 2.4}%` }} transition={{ delay: i * 0.06 + 0.1, duration: 0.5 }} />
            </div>
          </div>
          <span className="font-mono text-xs text-background/80 tabular-nums">{p.pct}%</span>
        </motion.div>
      ))}
    </div>
  );
}

function SlideResources() {
  return (
    <div className="border border-background/15 divide-y divide-background/15">
      <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-3 py-2 bg-background/8">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-background/55">Role</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-background/55">FTE</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-background/55">Rate</div>
      </div>
      {RESOURCES.map((r, i) => (
        <motion.div key={r.role}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: i * 0.08 }}
          className="grid grid-cols-[1fr_auto_auto] gap-4 px-3 py-2 items-center">
          <div className="text-sm text-background">{r.role}</div>
          <div className="font-mono text-sm text-emerald-glow tabular-nums">{r.fte}</div>
          <div className="font-mono text-sm text-background/85">{r.rate}</div>
        </motion.div>
      ))}
      <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-3 py-2 bg-background/8 text-background">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em]">Total crew</div>
        <div className="font-mono text-sm tabular-nums text-emerald-glow font-bold">48</div>
        <div className="font-mono text-sm">$5.2M labour</div>
      </div>
    </div>
  );
}

function SlideRisk() {
  const sevColor = (s) => s === 'high' ? '#ef4444' : s === 'med' ? 'hsl(43 96% 56%)' : 'hsl(151 100% 55%)';
  return (
    <div className="space-y-2">
      {RISKS.map((r, i) => (
        <motion.div key={r.code}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.08 }}
          className="flex items-center gap-3 border border-background/15 bg-background/5 px-3 py-2">
          <div className="font-mono text-[10px] tracking-[0.2em] text-background/55">{r.code}</div>
          <div className="flex-1 text-sm text-background">{r.label}</div>
          <div className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: sevColor(r.sev) }}>{r.sev}</div>
          <div className="font-mono text-sm font-semibold text-background tabular-nums">{r.impact}</div>
        </motion.div>
      ))}
      <div className="mt-3 flex items-center justify-between border-t border-background/15 pt-3">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-background/55">Contingency rollup</div>
        <div className="font-display text-2xl font-bold text-emerald-glow">+$1.95M</div>
      </div>
    </div>
  );
}

function SlideEVM() {
  return (
    <div className="grid grid-cols-3 gap-px bg-background/15 border border-background/15">
      {EVM.map((m, i) => (
        <motion.div key={m.k}
          initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.07 }}
          className={`bg-foreground p-4 ${m.warn ? 'ring-1 ring-amber-300/40' : ''}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-background/55">{m.k}</div>
          <div className={`mt-2 font-display text-2xl font-bold tabular-nums ${m.warn ? 'text-amber-300' : 'text-emerald-glow'}`} style={m.warn ? { color: 'hsl(43 96% 60%)' } : undefined}>
            {m.v}
          </div>
          <div className="mt-1 text-[11px] text-background/55">{m.hint}</div>
        </motion.div>
      ))}
    </div>
  );
}

function SlideFunding() {
  const max = Math.max(...FUNDING.flatMap(f => [f.plan, f.actual]));
  return (
    <div>
      <div className="border border-background/15 bg-background/5 p-4">
        <div className="flex items-end gap-2 h-32">
          {FUNDING.map((f, i) => (
            <div key={f.m} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end gap-0.5 h-full">
                <motion.div className="flex-1"
                  initial={{ height: 0 }} animate={{ height: `${(f.plan / max) * 100}%` }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
                <motion.div className="flex-1"
                  initial={{ height: 0 }} animate={{ height: `${(f.actual / max) * 100}%` }}
                  transition={{ delay: i * 0.05 + 0.05, duration: 0.4 }}
                  style={{ backgroundColor: 'hsl(151 100% 45%)' }} />
              </div>
              <div className="font-mono text-[9px] text-background/55">{f.m}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-4 text-[10px] font-mono uppercase tracking-[0.25em] text-background/65">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-3 inline-block bg-background/25" /> plan</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-3 inline-block" style={{ backgroundColor: 'hsl(151 100% 45%)' }} /> actual</span>
        </div>
      </div>
      <div className="mt-3 border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-start gap-3">
        <Activity className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: 'hsl(151 100% 55%)' }} />
        <div>
          <div className="text-sm font-semibold text-background">AI variance advisor</div>
          <div className="font-mono text-[11px] text-background/75 mt-0.5">
            Burn over plan by <span className="text-emerald-glow font-bold">+5.4%</span> at M-9. Re-baselined remaining 9 months at <span className="text-emerald-glow font-bold">$932k/mo</span> · front-loaded curve.
          </div>
        </div>
      </div>
    </div>
  );
}

const SLIDE_BODIES = {
  calc: SlideCalc,  wbs: SlideWBS,  res: SlideResources,
  risk: SlideRisk,  evm: SlideEVM,  fund: SlideFunding,
};

/* ---------------------------------------------------------------- *
 *  Main modal
 * ---------------------------------------------------------------- */
export default function DemoModal({ open, onClose, onCta }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // slideStart lives in a ref so it updates synchronously — no race with
  // the auto-advance effect after setIdx batched state changes.
  const slideStartRef = useRef(performance.now());
  // remember which idx we last reset for, so re-entering the auto-advance
  // effect under StrictMode (or any re-run with same idx) doesn't reset
  // the timer mid-slide.
  const lastResetIdxRef = useRef(-1);
  const [pausedElapsed, setPausedElapsed] = useState(0);
  const [, forceTick] = useState(0); // re-render trigger for progress bar

  // reset on open
  useEffect(() => {
    if (open) {
      setIdx(0);
      setPaused(false);
      setPausedElapsed(0);
      slideStartRef.current = performance.now();
      lastResetIdxRef.current = 0;
    }
  }, [open]);

  // Pause/resume — capture elapsed on pause, backdate slideStart on resume
  const togglePause = useCallback(() => {
    setPaused(prev => {
      if (!prev) {
        // pausing — freeze elapsed for THIS slide
        setPausedElapsed(performance.now() - slideStartRef.current);
      } else {
        // resuming — backdate slideStart so elapsed is preserved
        slideStartRef.current = performance.now() - pausedElapsed;
      }
      return !prev;
    });
  }, [pausedElapsed]);

  // Esc / arrows / space
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape')      onClose?.();
      if (e.key === 'ArrowRight')  setIdx(i => Math.min(SLIDES.length - 1, i + 1));
      if (e.key === 'ArrowLeft')   setIdx(i => Math.max(0, i - 1));
      if (e.key === ' ')           { e.preventDefault(); togglePause(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, togglePause]);

  // Auto-advance — owns the slide-start reset itself (no inter-effect race).
  // Single setTimeout per slide, cancelled on cleanup so StrictMode-double
  // mount only ever has ONE active timer.
  useEffect(() => {
    if (!open || paused) return;
    if (idx >= SLIDES.length - 1) return;
    // First time we enter a NEW idx (and not resuming from pause), reset the
    // slide start clock. We track the last reset idx in a ref so the effect
    // cleanup→re-run cycle within the same slide does NOT reset the clock.
    if (lastResetIdxRef.current !== idx) {
      slideStartRef.current = performance.now();
      lastResetIdxRef.current = idx;
    }
    const elapsed = performance.now() - slideStartRef.current;
    const remaining = Math.max(0, SLIDE_MS - elapsed);
    const t = setTimeout(() => setIdx(i => i + 1), remaining);
    return () => clearTimeout(t);
  }, [open, paused, idx]);

  // rAF-driven re-render so the progress bar updates smoothly
  useEffect(() => {
    if (!open || paused) return;
    let raf;
    const loop = () => { forceTick(t => t + 1); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [open, paused]);

  const slide = SLIDES[idx];
  const Body = SLIDE_BODIES[slide.id];
  const elapsed = paused
    ? pausedElapsed
    : Math.min(SLIDE_MS, performance.now() - slideStartRef.current);
  const totalProgress = Math.min(100, ((idx * SLIDE_MS + elapsed) / TOTAL_MS) * 100);

  const goPrev = useCallback(() => setIdx(i => Math.max(0, i - 1)), []);
  const goNext = useCallback(() => setIdx(i => Math.min(SLIDES.length - 1, i + 1)), []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="demo-modal-root"
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/85 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="demo-modal-backdrop"
          />
          {/* dialog */}
          <motion.div
            className="relative w-full max-w-4xl border border-foreground bg-foreground text-background shadow-hard-emerald overflow-hidden"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            role="dialog" aria-modal="true" aria-label="30-second product demo"
          >
            {/* progress bar */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-background/15">
              <motion.div className="h-full" style={{ width: `${totalProgress}%`, backgroundColor: 'hsl(151 100% 45%)' }} transition={{ ease: 'linear' }} />
            </div>

            {/* header */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-background/15">
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex items-center gap-2 border border-background/30 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.25em]">
                  <span className="h-1.5 w-1.5 inline-block" style={{ backgroundColor: 'hsl(151 100% 45%)' }} />
                  Live demo · 30s
                </span>
                <div className="hidden sm:block font-mono text-[11px] text-background/55 truncate">
                  Sample · {SAMPLE.project} · {SAMPLE.contract}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={togglePause}
                  className="p-2 hover:bg-background/10 transition-colors"
                  aria-label={paused ? 'Resume demo' : 'Pause demo'}
                  data-testid="demo-pause-btn"
                >
                  {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                </button>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-background/10 transition-colors"
                  aria-label="Close demo"
                  data-testid="demo-close-btn"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* body */}
            <div className="grid md:grid-cols-[200px_1fr]">
              {/* steps rail */}
              <div className="hidden md:flex flex-col border-r border-background/15">
                {SLIDES.map((s, i) => {
                  const Icon = s.icon;
                  const active = i === idx;
                  const done = i < idx;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setIdx(i)}
                      className={`flex items-center gap-3 px-4 py-3 border-b border-background/10 text-left transition-colors ${
                        active ? 'bg-background/8' : 'hover:bg-background/5'
                      }`}
                      data-testid={`demo-step-${s.id}`}
                    >
                      <span className={`inline-flex h-7 w-7 items-center justify-center border ${
                        active ? 'border-emerald-500 text-background' : done ? 'border-background/30 text-background/55' : 'border-background/20 text-background/45'
                      }`} style={active ? { borderColor: 'hsl(151 100% 45%)', backgroundColor: 'hsl(151 100% 45% / 0.12)' } : undefined}>
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </span>
                      <div className="min-w-0">
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-background/55">{s.badge}</div>
                        <div className={`text-sm leading-tight ${active ? 'text-background font-semibold' : 'text-background/75'}`}>{s.title}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* slide stage */}
              <div className="px-5 sm:px-7 py-6 min-h-[360px] sm:min-h-[420px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={slide.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.28 }}
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-glow">/ {slide.badge}</div>
                    <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight">{slide.title}</h3>
                    <p className="mt-1 text-sm text-background/65" dangerouslySetInnerHTML={{ __html: slide.kicker }} />
                    <div className="mt-5">
                      <Body />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* footer controls */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-3 border-t border-background/15">
              <div className="flex items-center gap-1.5" data-testid="demo-dots">
                {SLIDES.map((s, i) => (
                  <button
                    key={s.id} onClick={() => setIdx(i)}
                    aria-label={`Jump to slide ${i + 1}`}
                    data-testid={`demo-dot-${i}`}
                    className="h-1.5 transition-all"
                    style={{
                      width: i === idx ? 28 : 12,
                      backgroundColor: i <= idx ? 'hsl(151 100% 45%)' : 'rgba(255,255,255,0.2)',
                    }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={goPrev} disabled={idx === 0}
                  className="p-2 border border-background/20 hover:bg-background/10 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous slide" data-testid="demo-prev-btn">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {idx < SLIDES.length - 1 ? (
                  <button onClick={goNext}
                    className="px-4 py-2 border border-background/20 hover:bg-background/10 font-display text-xs tracking-wider uppercase font-semibold inline-flex items-center gap-1.5"
                    data-testid="demo-next-btn">
                    Next <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <button onClick={onCta}
                    className="px-4 py-2 font-display text-xs tracking-wider uppercase font-semibold inline-flex items-center gap-1.5"
                    style={{ backgroundColor: 'hsl(151 100% 45%)', color: '#000' }}
                    data-testid="demo-cta-btn">
                    Try it free <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
